import {
  collapseBookingStatus,
  type BookingState,
  type StatusKey,
  type StatusTone,
} from "@/modules/booking/surfaces";

/**
 * The **web's words** for a booking's effective state.
 *
 * The collapse itself (which states become which of four outcomes, and the
 * tone) moved into `booking/domain/statusCollapse.ts` in R22 T9, because it is
 * a rule the server ships rather than a rendering: it used to live here, which
 * worked while the web was its only reader and stopped working when the mobile
 * wire format needed it too. What is left here is what genuinely belongs to
 * this client: the sentences, and the Tailwind classes for the tones.
 *
 * See the domain module for why there are four outcomes and not eight.
 */
export type { StatusKey, StatusTone };

/** The web's sentence per outcome. A new locale changes this file, not the API. */
const LABEL: Record<StatusKey, string> = {
  awaiting: "Awaiting confirmation",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
};

export function bookingStatus(state: BookingState): {
  key: StatusKey;
  label: string;
  tone: StatusTone;
} {
  const { key, tone } = collapseBookingStatus(state);
  return { key, label: LABEL[key], tone };
}

/**
 * Two hues and a neutral, no more. Confirmed is the accent, cancelled is red,
 * completed is neutral, and pending is an outline rather than a colour: it is
 * the absence of a decision, so it should not compete with one.
 */
export const TONE_CLASS: Record<StatusTone, string> = {
  success: "bg-accent/12 text-accent",
  danger: "bg-danger/12 text-danger",
  warning: "border border-line bg-transparent text-body",
  info: "bg-accent/12 text-accent",
  muted: "bg-line text-muted",
};

/**
 * The **colour badge** each tone wears in a booking row, and the stripe down
 * the row's leading edge. Every value is a theme token, so dark mode follows
 * on its own. Four outcomes, four distinct fills: awaiting is the ink (navy, or
 * near-white in dark), confirmed the accent, completed the neutral, cancelled
 * red. The badge carries no words, so each also has its own glyph (see
 * `StatusColorBadge`): meaning never rests on colour alone (§10).
 */
export const STATUS_COLOR: Record<StatusTone, { fill: string; stripe: string }> = {
  warning: { fill: "bg-ink", stripe: "border-l-ink" },
  info: { fill: "bg-accent", stripe: "border-l-accent" },
  success: { fill: "bg-accent", stripe: "border-l-accent" },
  muted: { fill: "bg-muted", stripe: "border-l-muted" },
  danger: { fill: "bg-danger", stripe: "border-l-danger" },
};

/**
 * The booking state machine's own names, for the colour guide. A record over
 * `BookingState` rather than a list, so a ninth state is a compile error here
 * rather than a colour the guide forgets to explain.
 */
const STATE_NAME: Record<BookingState, string> = {
  requested: "Requested",
  approved: "Approved",
  completed: "Completed",
  declined: "Declined",
  expired: "Expired",
  withdrawn: "Withdrawn",
  cancelled_by_consumer: "Cancelled by the customer",
  cancelled_by_business: "Cancelled by the clinic",
};

/** What each colour means, in a sentence. The mobile apps port these. */
const MEANING: Record<StatusKey, string> = {
  awaiting:
    "Requested and waiting for the clinic to approve it. No time is held until then.",
  confirmed: "The clinic approved it. The appointment is booked.",
  completed: "The appointment has taken place.",
  cancelled:
    "The appointment is not happening: the clinic declined it, either side cancelled it, the request was withdrawn, or it expired unanswered.",
};

const GUIDE_ORDER: StatusKey[] = ["awaiting", "confirmed", "completed", "cancelled"];

export interface StatusGuideEntry {
  key: StatusKey;
  label: string;
  tone: StatusTone;
  meaning: string;
  /** The booking states that show this colour, in the machine's names. */
  states: string[];
}

/**
 * The colour guide, **derived from the collapse** rather than written beside
 * it: each state is placed under whichever outcome `collapseBookingStatus`
 * sends it to, so the guide cannot disagree with the badges it explains.
 */
export function statusGuide(): StatusGuideEntry[] {
  const states = Object.keys(STATE_NAME) as BookingState[];
  return GUIDE_ORDER.map((key) => {
    const members = states.filter((s) => collapseBookingStatus(s).key === key);
    return {
      key,
      label: LABEL[key],
      tone: collapseBookingStatus(members[0]!).tone,
      meaning: MEANING[key],
      states: members.map((s) => STATE_NAME[s]),
    };
  });
}
